import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/lib/auth';
import { BrandingProvider } from '@/lib/branding';
import { Toaster } from 'sonner';

import LoginPage from '@/pages/LoginPage';
import AppLayout from '@/components/layout/AppLayout';
import DashboardPage from '@/pages/DashboardPage';
import ExecutiveCommandCenter from '@/pages/ExecutiveCommandCenter';
import AgendaListPage from '@/pages/AgendaListPage';
import AgendaCalendarPage from '@/pages/AgendaCalendarPage';
import AgendaFormPage from '@/pages/AgendaFormPage';
import AgendaDetailPage from '@/pages/AgendaDetailPage';
import ApprovalInboxPage from '@/pages/ApprovalInboxPage';
import SPPDListPage from '@/pages/SPPDListPage';
import SPPDFormPage from '@/pages/SPPDFormPage';
import SPPDDetailPage from '@/pages/SPPDDetailPage';
import ReportsPage from '@/pages/ReportsPage';
import AuditTrailPage from '@/pages/AuditTrailPage';
import UserManagementPage from '@/pages/UserManagementPage';
import OPDManagementPage from '@/pages/OPDManagementPage';
import PejabatManagementPage from '@/pages/PejabatManagementPage';
import PejabatDetailPage from '@/pages/PejabatDetailPage';
import KeberadaanPage from '@/pages/KeberadaanPage';
import NotificationsPage from '@/pages/NotificationsPage';
import ProfilePage from '@/pages/ProfilePage';
import SearchResultsPage from '@/pages/SearchResultsPage';
import BrandingSettingsPage from '@/pages/BrandingSettingsPage';
import WhatsAppSettingsPage from '@/pages/WhatsAppSettingsPage';
import WhatsAppLogPage from '@/pages/WhatsAppLogPage';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'pimpinan') return <Navigate to="/command-center" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<RootRedirect />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/command-center" element={<ExecutiveCommandCenter />} />
              <Route path="/keberadaan" element={<KeberadaanPage />} />
              <Route path="/agenda" element={<AgendaListPage />} />
              <Route path="/agenda/kalender" element={<AgendaCalendarPage />} />
              <Route path="/agenda/baru" element={<ProtectedRoute roles={['admin', 'operator']}><AgendaFormPage /></ProtectedRoute>} />
              <Route path="/agenda/edit/:id" element={<ProtectedRoute roles={['admin', 'operator']}><AgendaFormPage /></ProtectedRoute>} />
              <Route path="/agenda/:id" element={<AgendaDetailPage />} />
              <Route path="/approval" element={<ProtectedRoute roles={['admin', 'kabag']}><ApprovalInboxPage /></ProtectedRoute>} />
              <Route path="/sppd" element={<SPPDListPage />} />
              <Route path="/sppd/baru" element={<ProtectedRoute roles={['admin', 'operator']}><SPPDFormPage /></ProtectedRoute>} />
              <Route path="/sppd/edit/:id" element={<ProtectedRoute roles={['admin', 'operator']}><SPPDFormPage /></ProtectedRoute>} />
              <Route path="/sppd/:id" element={<SPPDDetailPage />} />
              <Route path="/laporan" element={<ReportsPage />} />
              <Route path="/audit" element={<ProtectedRoute roles={['admin', 'kabag', 'pimpinan']}><AuditTrailPage /></ProtectedRoute>} />
              <Route path="/manajemen/pejabat" element={<ProtectedRoute roles={['admin', 'operator']}><PejabatManagementPage /></ProtectedRoute>} />
              <Route path="/manajemen/pejabat/:id" element={<ProtectedRoute roles={['admin', 'operator', 'kabag']}><PejabatDetailPage /></ProtectedRoute>} />
              <Route path="/manajemen/user" element={<ProtectedRoute roles={['admin']}><UserManagementPage /></ProtectedRoute>} />
              <Route path="/manajemen/opd" element={<ProtectedRoute roles={['admin']}><OPDManagementPage /></ProtectedRoute>} />
              <Route path="/pengaturan/branding" element={<ProtectedRoute roles={['admin']}><BrandingSettingsPage /></ProtectedRoute>} />
              <Route path="/pengaturan/whatsapp" element={<ProtectedRoute roles={['admin']}><WhatsAppSettingsPage /></ProtectedRoute>} />
              <Route path="/pengaturan/whatsapp/logs" element={<ProtectedRoute roles={['admin', 'kabag', 'pimpinan']}><WhatsAppLogPage /></ProtectedRoute>} />
              <Route path="/notifikasi" element={<NotificationsPage />} />
              <Route path="/profil" element={<ProfilePage />} />
              <Route path="/pencarian" element={<SearchResultsPage />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
  );
}
